import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { AxiosError } from 'axios';
import clsx from 'clsx';
import dayjs, { Dayjs } from 'dayjs';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppSelector, useToast } from '../../hooks';
import { ModeCalendar } from '../../pages/ListManyCalendars';
import { ReportInfo } from '../../pages/ListReports';
import { apiService } from '../../services/api';
import {
  AllUserRes,
  ClassEvent,
  CreateClassEventReq,
  DeleteEventReq,
  DeleteTypes,
  EventDataIds,
  IntervalType,
  Level,
  PaymentStatus,
  PlaceType,
  RecurrenceTypes,
  SubjectEntity,
  UserRole,
} from '../../services/api.types';
import { usersService } from '../../services/users.service';
import { dateLabelBlack } from '../../theme';
import {
  buildCreateClass,
  buildDeleteClass,
  buildRecurrenceDefaultValues,
  getRecurrenceTypeFromEvent,
  isSameRecurrence,
} from '../../utils/classes';
import { getEndHour, mergeDateAndHour } from '../../utils/date';
import {
  formatAddress,
  formatMoney,
  formatStringMoney,
} from '../../utils/formatters';
import { Button } from '../Button';

import { ClassOrTeacherBusyType } from '../Calendar';
import { Datepicker } from '../Datepicker';
import { DraggableModal } from '../DraggableModal';
import { InputAutocomplete } from '../Inputs/Autocomplete';
import { Input } from '../Inputs/Input';
import { TextArea } from '../Inputs/TextArea';
import { Radio } from '../Radio';
import { DataAutocomplete } from '../SelectResponsibles';
import { TeacherReport } from '../TeacherReport';
import { Timepicker } from '../Timepicker';
import {
  ClassEventConfirmationModal,
  ClassEventConfirmationModalValues,
} from './ClassEventConfirmationModal';
import { eventRules } from './EventRules';
import { ModalRecurrence } from './ModalRecurrence';
import {
  UpdateClassDateModal,
  UpdateClassDateModalProps,
} from './UpdateClassDateModal';

interface ResponsibleToStudent {
  id: number;
  addressComplement?: string;
  addressNumber?: string;
  postalCode?: string;
  responsibleFullName?: string;
}

interface TeachersSubjects {
  id: number;
  subjectsTaught?: SubjectEntity[];
}

export interface EventCalendarValues {
  startDate: Dayjs | null;
  startHour: Dayjs | null;
  endHour: Dayjs | null;
  weekdays: {
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
    sunday: boolean;
  };
  bypassConflict?: boolean;
  bypassPaymentClass?: boolean;
  intervalType: IntervalType;
  intervalNumber: string;
  recurrenceType: RecurrenceTypes;
  count?: number;
  until?: Dayjs | null;
  student: DataAutocomplete | null;
  teacher: DataAutocomplete | null;
  responsible?: string;
  subject: DataAutocomplete | null;
  placeType: PlaceType | null; // Allows no option to be selected initially
  place: string;
  description: string;
  teacherObservations?: string;
  changeValueJustification?: string;
  valueFromResponsible: string;
  valueToTeacher: string;
  valueToTrip: string;
  updateClassParams?: DeleteEventReq;
}

export const makeLevelLabel: Record<Level, (subjectName: string) => string> = {
  [Level.KINDERGARTEN]: (subjectName) => `${subjectName} - Jardim de Infância`,
  [Level.ELEMENTARY_1]: (subjectName) =>
    `${subjectName} - Ensino Fundamental 1`,
  [Level.ELEMENTARY_2]: (subjectName) =>
    `${subjectName} - Ensino Fundamental 2`,
  [Level.HIGH_SCHOOL]: (subjectName) => `${subjectName} - Ensino Médio`,
  [Level.UNIVERSITY]: (subjectName) => `${subjectName} - Ensino Superior`,
  [Level.TECHNICAL]: (subjectName) => `${subjectName} - Curso Técnico`,
};

export const translateSubjectLevel: Record<Level, string> = {
  [Level.KINDERGARTEN]: 'Jardim de Infância',
  [Level.ELEMENTARY_1]: 'Ensino Fundamental 1',
  [Level.ELEMENTARY_2]: 'Ensino Fundamental 2',
  [Level.HIGH_SCHOOL]: 'Ensino Médio',
  [Level.UNIVERSITY]: 'Ensino Superior',
  [Level.TECHNICAL]: 'Curso Técnico',
};

interface EventCalendarProps {
  open: boolean;
  onClose: () => void;
  eventInfo: {
    mode: ModeCalendar;
    event:
      | ({
          title: string;
          start: Date;
          end: Date;
        } & ClassEvent)
      | null;
  };
  onRemove?: (event: ClassOrTeacherBusyType) => void | Promise<void>;
  onCreate?: (event: ClassOrTeacherBusyType) => void | Promise<void>;
  onUpdate?: (
    oldEvent: ClassOrTeacherBusyType,
    newEvent: ClassOrTeacherBusyType
  ) => void | Promise<void>;
}

export const CalendarEventForm = ({
  open,
  onClose,
  eventInfo,
  onRemove,
  onCreate,
  onUpdate,
}: EventCalendarProps) => {
  const event = eventInfo.event;
  const mode = eventInfo.mode;

  const user = useAppSelector((state) => state.user);
  const toast = useToast();
  const isAdminOrEmployee =
    user?.role === UserRole.ADMIN || user?.role === UserRole.EMPLOYEE;
  const isTeacher = user?.role === UserRole.TEACHER;
  const isStudent = user?.role === UserRole.STUDENT;

  const isEventBeforeNow = useMemo(() => {
    if (!event) return false;
    return dayjs().isAfter(event.startDate);
  }, [event]);
  const recurrenceType = useMemo(
    () => getRecurrenceTypeFromEvent(event),
    [event]
  );

  const eventDefaultValues: EventCalendarValues = useMemo(() => {
    const recurrenceDefaultValues = buildRecurrenceDefaultValues(
      recurrenceType,
      event
    );

    const eventCalendarValues: EventCalendarValues = {
      ...recurrenceDefaultValues,
      student: null,
      responsible: event?.responsible?.fullName ?? '',
      teacher: null,
      subject: null,
      placeType: null,
      place: '',
      description: '',
      teacherObservations: '',
      changeValueJustification: '',
      valueFromResponsible: '',
      valueToTeacher: '',
      valueToTrip: '',
    };

    if (event?.teacher) {
      eventCalendarValues.teacher = {
        id: event.teacher.id,
        displayName: event.teacher.fullName,
      };
    }

    if (event?.subject) {
      eventCalendarValues.subject = {
        id: event.subject.id,
        displayName: makeLevelLabel[event.subject.level](event.subject.name),
      };
    }

    if (event?.student) {
      eventCalendarValues.student = {
        id: event.student.id,
        displayName: event.student.fullName,
      };
    }

    eventCalendarValues.placeType = event?.placeType ?? null;
    eventCalendarValues.place = event?.place ?? '';
    eventCalendarValues.description = event?.description ?? '';
    eventCalendarValues.teacherObservations = event?.teacherObservations ?? '';
    eventCalendarValues.changeValueJustification =
      event?.changeValueJustification ?? '';
    eventCalendarValues.valueFromResponsible = formatMoney(
      event?.valueFromResponsible ?? 0
    );
    eventCalendarValues.valueToTeacher = formatMoney(
      event?.valueToTeacher ?? 0
    );
    eventCalendarValues.valueToTrip = formatMoney(event?.valueToTrip ?? 0);

    return eventCalendarValues;
  }, [event, recurrenceType]);

  const eventForm = useForm<EventCalendarValues>({
    mode: 'all',
    defaultValues: eventDefaultValues,
  });
  const createClassConfirmationModalForm =
    useForm<ClassEventConfirmationModalValues>({
      mode: 'all',
      defaultValues: {
        classTimeConflictConsent: false,
        paymentClassConsent: false,
        changeValueJustification: eventDefaultValues.changeValueJustification,
      },
    });

  const student = eventForm.watch('student');
  const teacher = eventForm.watch('teacher');
  const placeType = eventForm.watch('placeType');

  const eventValueFromResponsible = eventForm.watch('valueFromResponsible');
  const eventValueToTeacher = eventForm.watch('valueToTeacher');
  const eventValueToTrip = eventForm.watch('valueToTrip');
  const changeValueJustification = eventForm.watch('changeValueJustification');

  const [students, setStudents] = useState<DataAutocomplete[]>([]);
  // NOTE: Caching the address is only used for non-responsible students, as the
  // responsible students already have their address stored in the responsibles
  // array, thus avoiding unnecessary API calls by default
  const [cachedStudentAddress, setCachedStudentAddress] = useState<string>('');
  const [responsibles, setResponsibles] = useState<ResponsibleToStudent[]>([]);
  const [teachers, setTeachers] = useState<DataAutocomplete[]>([]);
  const [valueToTrip, setValueToTrip] = useState<number>(0);
  const [teachersSubjects, setTeachersSubjects] = useState<TeachersSubjects[]>(
    []
  );
  const [startedClassSubmission, setStartedClassSubmission] =
    useState<boolean>(false);

  const [openCreateConflictBypassModal, setOpenCreateConflictBypassModal] =
    useState<boolean>(false);
  const [openJustificationModal, setOpenJustificationModal] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openUpdateClass, setOpenUpdateClass] = useState(false);
  const [handleUpdateClass, setHandleUpdateClass] = useState<{
    mode: UpdateClassDateModalProps['mode'];
    availableDeleteTypeOptions: UpdateClassDateModalProps['availableOptions'];
    func: (
      type: DeleteTypes,
      changeValueJustification?: string,
      gaveBypassConflictConsent?: boolean
    ) => Promise<void>;
  }>({
    mode: 'delete',
    availableDeleteTypeOptions: [DeleteTypes.DATE, DeleteTypes.UNTIL],
    func: async () => undefined,
  });
  const [deleteType, setDeleteType] = useState<DeleteTypes>(DeleteTypes.DATE);
  const [reportInfo, setReportInfo] = useState<ReportInfo | undefined>();
  const [reportInfoOpen, setReportInfoOpen] = useState<boolean>(false);
  const [hasConflict, setHasConflict] = useState<boolean>(false);
  const [paymentStatusesInRange, setPaymentStatusesInRange] = useState<
    PaymentStatus[]
  >([]);

  const updatedSomeValue =
    eventDefaultValues.valueFromResponsible !== eventValueFromResponsible ||
    eventDefaultValues.valueToTeacher !== eventValueToTeacher ||
    eventDefaultValues.valueToTrip !== eventValueToTrip;
  const thereWasJustification = !!eventDefaultValues.changeValueJustification;

  const needsValueJustification = useMemo(() => {
    if (!isAdminOrEmployee || mode === ModeCalendar.create) return false;

    return (
      (updatedSomeValue && startedClassSubmission) ||
      thereWasJustification ||
      !!changeValueJustification
    );
  }, [
    changeValueJustification,
    isAdminOrEmployee,
    mode,
    startedClassSubmission,
    thereWasJustification,
    updatedSomeValue,
  ]);
  const showValueJustificationModalButton = useMemo(() => {
    return needsValueJustification && changeValueJustification !== '';
  }, [changeValueJustification, needsValueJustification]);

  const hasConflicts = async (data: CreateClassEventReq) => {
    setIsLoading(true);
    const classEventInfo = {
      eventId: event?.id,
      studentId: data.studentId,
      teacherId: data.teacherId,
      startDate: data.startDate,
      endDate: dayjs(data.startDate).add(data.duration, 'minute').toDate(),
      startHour: data.startHour,
      duration: data.duration,
      rrule: data.recurrenceRule,
    };
    return await apiService
      .checkConflict(classEventInfo)
      .then((res) => {
        return res;
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCreate = async (data: CreateClassEventReq) => {
    setIsLoading(true);
    const hasConflictsRes = await hasConflicts(data);
    if (hasConflictsRes) {
      toast.error('Conflito de horário encontrado');
      return;
    }
    await apiService
      .createEvent(data)
      .then(async (res) => {
        await onCreate?.(res);
        toast.success('Evento criado!');
        if (isAdminOrEmployee && res.roomAlert === 'CRITICAL') {
          toast.error(
            '🚨 Crítico: há 10 ou mais aulas simultâneas no curso neste momento!'
          );
        } else if (isAdminOrEmployee && res.roomAlert === 'WARNING') {
          toast.warning(
            '⚠️ Atenção: há 7 ou mais aulas simultâneas no curso neste momento. Verifique a disponibilidade de salas.'
          );
        }
        onClose();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleResponsibleFullName = (studentId?: number) => {
    const responsibleStudent = responsibles.find((r) => r.id === studentId);
    return responsibleStudent?.responsibleFullName;
  };

  const handleChangeEvent = async (res: ClassEvent) => {
    event && (await onUpdate?.(event, res));
  };

  const handleEdit = async (
    type: DeleteTypes,
    changeValueJustification?: string,
    gaveBypassConflictConsent?: boolean,
    gaveBypassPaymentClassConsent?: boolean
  ) => {
    // Justification text area in UpdateClassDateModal
    if (changeValueJustification)
      eventForm.setValue('changeValueJustification', changeValueJustification);
    if (gaveBypassConflictConsent) eventForm.setValue('bypassConflict', true);
    if (gaveBypassPaymentClassConsent)
      eventForm.setValue('bypassPaymentClass', true);

    const values = eventForm.getValues();
    const recurrence =
      type === DeleteTypes.DATE ? RecurrenceTypes.ONCE : values.recurrenceType;

    const createClassParams = await buildCreateClass({
      ...values,
      recurrenceType: recurrence,
    }).catch((error) => {
      toast.error(error);
    });
    const updateClassParams = buildDeleteClass(
      type,
      event,
      gaveBypassPaymentClassConsent
    );

    if (!createClassParams) return;

    const hasConflictsRes = await hasConflicts(createClassParams);
    if (hasConflictsRes) {
      toast.error('Conflito de horário encontrado');
      return;
    }
    setIsLoading(true);
    await apiService
      .updateEvent(Number(event?.id), {
        ...createClassParams,
        updateClassType: updateClassParams.removeDateRecurrence,
        updateClassDate: updateClassParams.date,
      })
      .then(async (res) => {
        await handleChangeEvent(res);
        toast.success('Evento alterado!');
        setOpenUpdateClass(false);
        if (isAdminOrEmployee && res.roomAlert === 'CRITICAL') {
          toast.error(
            '🚨 Crítico: há 10 ou mais aulas simultâneas no curso neste momento!'
          );
        } else if (isAdminOrEmployee && res.roomAlert === 'WARNING') {
          toast.warning(
            '⚠️ Atenção: há 7 ou mais aulas simultâneas no curso neste momento. Verifique a disponibilidade de salas.'
          );
        }
        onClose();
      })
      .catch((error: AxiosError) => {
        const data = error.response?.data as { message: string };
        if (data?.message?.includes('Conflito de horário')) {
          setDeleteType(type);
          setHasConflict(true);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const teacherData = eventForm.watch('teacher');
  const studentData = eventForm.watch('student');
  const subjectData = eventForm.watch('subject');

  const showValueFromResponsible =
    isAdminOrEmployee || user?.role === UserRole.RESPONSIBLE;
  const showValueToTeacher = isAdminOrEmployee || isTeacher;
  const showValueToTrip = !!eventForm.getValues('valueToTrip');

  useEffect(() => {
    if (mode !== ModeCalendar.create) return;

    if (teacherData && studentData && subjectData) {
      const data: EventDataIds = {
        teacherId: teacherData.id,
        studentId: studentData.id,
        subjectId: subjectData.id,
      };
      apiService.getDefaultData(data).then((res) => {
        eventForm.setValue(
          'valueToTeacher',
          formatMoney(res.valueToTeacher ?? 0)
        );
        eventForm.setValue(
          'valueFromResponsible',
          formatMoney(res.valueFromResponsible ?? 0)
        );
      });
    }
  }, [teacherData, studentData, subjectData, mode]);

  useEffect(() => {
    if (mode !== ModeCalendar.view) {
      apiService.getValueToTrip().then((res) => {
        setValueToTrip(res.tripValue);
      });
    }
  }, []);

  // Check if the class is paid
  useEffect(() => {
    if (!event?.id || !isAdminOrEmployee) return;

    // just to ensure date is set correctly
    const deleteClass = buildDeleteClass(deleteType, event);

    setIsLoading(true);
    apiService
      .getReportPayments(Number(event?.id), {
        eventDate: deleteClass.date,
        deleteType: deleteClass.removeDateRecurrence,
      })
      .then((paymentStatuses) => {
        setPaymentStatusesInRange(paymentStatuses);
      })
      .finally(() => setIsLoading(false));
  }, [event, deleteType, isAdminOrEmployee]);

  const handleDelete = async (
    type: DeleteTypes,
    changeValueJustification?: string,
    _?: boolean, // No conflict confirmation required for delete operation
    gaveBypassPaymentClassConsent?: boolean
  ) => {
    // Justification text area in UpdateClassDateModal
    if (changeValueJustification)
      eventForm.setValue('changeValueJustification', changeValueJustification);

    const deleteClassReq = buildDeleteClass(
      type,
      event,
      gaveBypassPaymentClassConsent
    );

    setIsLoading(true);
    const updatedEvent = await apiService
      .deleteEvent(Number(event?.id), deleteClassReq)
      .then((res) => {
        toast.success('Evento Excluído!');
        return res;
      })
      .finally(() => {
        setIsLoading(false);
      });

    await onRemove?.(updatedEvent);
    setOpenUpdateClass(false);
    onClose();
  };

  const onSubmit = async (data: EventCalendarValues) => {
    const createClassParams = await buildCreateClass(data).catch((error) => {
      toast.error(error.message);
    });

    if (!createClassParams) return;

    setStartedClassSubmission(true);

    if (mode === ModeCalendar.edit && openUpdateClass) {
      await handleEdit(deleteType);
      setOpenJustificationModal(updatedSomeValue);
    } else if (mode === ModeCalendar.edit) {
      const availableOptions = [];
      const isSameClassRecurrence = isSameRecurrence(
        event,
        eventForm.getValues(),
        true
      );
      if (isEventBeforeNow) {
        // before now
        availableOptions.push(DeleteTypes.DATE);
      } else if (!isSameClassRecurrence) {
        // after now and different recurrence
        availableOptions.push(DeleteTypes.UNTIL);
      } else {
        // after now and same recurrence
        availableOptions.push(DeleteTypes.DATE, DeleteTypes.UNTIL);
      }
      setOpenUpdateClass(true);
      setHandleUpdateClass({
        mode: 'update',
        availableDeleteTypeOptions: availableOptions,
        func: handleEdit,
      });
    } else if (mode === ModeCalendar.create) {
      await handleCreate(createClassParams);
    }
  };

  // Address field
  useEffect(() => {
    const isSamePlaceType =
      (placeType === PlaceType.PLUS_HEADQUARTER &&
        event?.placeType === PlaceType.PLUS_HEADQUARTER) ||
      (placeType === PlaceType.STUDENT_HOME &&
        event?.placeType === PlaceType.STUDENT_HOME) ||
      (placeType === PlaceType.REMOTE && event?.placeType === PlaceType.REMOTE);

    function setHeadquartersAddress() {
      eventForm.setValue(
        'place',
        'SCRS 504, Bloco C, lojas 9/10 - entrada pela w2 sul'
      );
      eventForm.setValue('valueToTrip', formatMoney(0));
    }

    function setRemoteAddress() {
      eventForm.setValue('place', 'Remoto');
      eventForm.setValue('valueToTrip', formatMoney(0));
    }

    async function setStudentAddress(id: number) {
      if (cachedStudentAddress.length !== 0) {
        eventForm.setValue('place', cachedStudentAddress);
        return;
      }

      const address = await apiService.getStudentAddress(id);
      const addressString = await formatAddress(
        address.postalCode,
        address.addressComplement,
        address.addressNumber
      );

      eventForm.setValue('place', addressString);
      setCachedStudentAddress(addressString);
    }

    async function setStudentResponsibleAddress(
      studentResponsible: ResponsibleToStudent
    ) {
      const addressString = await formatAddress(
        studentResponsible.postalCode,
        studentResponsible.addressComplement,
        studentResponsible.addressNumber
      );

      eventForm.setValue('place', addressString);
    }

    if (isSamePlaceType) {
      // use old values as those values can be customized
      eventForm.setValue('place', event.place);
      eventForm.setValue('valueToTrip', formatMoney(event.valueToTrip));
      return;
    }

    switch (placeType) {
      case PlaceType.REMOTE:
        setRemoteAddress();
        break;
      case PlaceType.PLUS_HEADQUARTER:
        setHeadquartersAddress();
        break;
      case PlaceType.STUDENT_HOME: {
        eventForm.setValue('valueToTrip', formatMoney(valueToTrip));
        const responsibleStudent = responsibles.find(
          (r) => r.id === Number(student?.id)
        );
        if (responsibleStudent) {
          setStudentResponsibleAddress(responsibleStudent);
        } else if (student) {
          setStudentAddress(student.id);
        }
        break;
      }
    }
  }, [placeType, student, responsibles, event]);

  useEffect(() => {
    if (mode !== ModeCalendar.create) return;

    usersService
      .getAllUsers({ role: UserRole.TEACHER })
      .then((response: AllUserRes) => {
        setTeachers(
          response.users.map((user) => ({
            id: user.id,
            displayName: user.fullName,
          }))
        );
        setTeachersSubjects(
          response.users.map((user) => ({
            id: user.id,
            subjectsTaught: user.teacher?.subjectsTaught,
          }))
        );
      });

    usersService
      .getAllUsers({ role: UserRole.STUDENT })
      .then((response: AllUserRes) => {
        setStudents(
          response.users.map((user) => ({
            id: user.id,
            displayName: user.fullName,
          }))
        );
        setResponsibles(
          response.users.map((user) => ({
            postalCode: user.student?.responsible.postalCode,
            addressComplement: user.student?.responsible.addressComplement,
            addressNumber: user.student?.responsible.addressNumber,
            id: user.id,
            responsibleFullName: user.student?.responsible.user?.fullName,
          }))
        );
      });
  }, [mode]);

  const selectSubjects: DataAutocomplete[] = useMemo(() => {
    const selectTeacher = teachersSubjects.find((value) => {
      return value.id === Number(teacher?.id);
    });
    if (selectTeacher?.subjectsTaught) {
      return selectTeacher.subjectsTaught.map((subject) => ({
        id: subject.id,
        displayName: makeLevelLabel[subject.level](subject.name),
      }));
    }
    return [];
  }, [teacher?.id, teachersSubjects]);

  const handleChangeMoneyMask = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    field: keyof EventCalendarValues
  ) => {
    eventForm.setValue(field, formatStringMoney(event.target.value));
  };

  const showReportButton = useMemo(() => {
    if (mode === ModeCalendar.create) return false;
    if (isStudent) return false;

    const startDate = eventForm.getValues('startDate')?.toDate();
    const endHour = eventForm.getValues('endHour')?.toDate();
    if (!startDate || !endHour) return false;

    const end = mergeDateAndHour(startDate, endHour);
    return dayjs().isAfter(end);
  }, [mode, eventForm]);

  const modalTitle = useMemo(() => {
    if (mode === ModeCalendar.create) return 'Criar evento';
    if (mode === ModeCalendar.edit) return 'Editar evento';
    return 'Visualizar evento';
  }, [mode]);

  const handleOnOpenReport = async () => {
    if (!event?.isClass) return;

    await apiService
      .getOrCreateReportEvent(
        Number(event?.id),
        new Date(String(event?.startDate))
      )
      .then((res) => {
        setReportInfo({
          id: res.id,
          classLocation: res.place,
          date: res.date,
          email: String(event?.teacher.email),
          startTimeClass: res.startHour,
          endTimeClass: getEndHour(res.startHour, res.duration),
          fullName: String(event?.teacher.fullName),
          studentName: String(event?.student.fullName),
          responsibleFullName: String(event.responsible?.fullName),
          generalObservation: res.observations,
          status: res.status,
          studentPerformance: res.studentPerformance,
          taughtContent: res.taughtContent,
          reviewRequest: res.reviewRequest,
          valueFromResponsible: res.valueFromResponsible,
          valueToTeacher: res.valueToTeacher,
          valueToTrip: res.valueToTrip,
          wasStudentAbsent: res.wasStudentAbsent,
        });
        setReportInfoOpen(true);
      });
  };

  return (
    <>
      <DraggableModal
        title={modalTitle}
        open={open}
        mode={mode}
        onOpenReport={showReportButton ? handleOnOpenReport : undefined}
        onClose={
          !isLoading
            ? () => {
                onClose();
              }
            : () => undefined
        }
        onDelete={() => {
          setOpenUpdateClass(true);
          setHandleUpdateClass({
            mode: 'delete',
            availableDeleteTypeOptions: [DeleteTypes.DATE, DeleteTypes.UNTIL],
            func: handleDelete,
          });
        }}
        onJustificateChangeValue={
          showValueJustificationModalButton
            ? () => setOpenJustificationModal(true)
            : undefined
        }
      >
        <FormProvider {...eventForm}>
          <form
            onSubmit={eventForm.handleSubmit(onSubmit)}
            className="w-full flex flex-col items-center justify-between p-6 gap-4"
          >
            <div
              className={clsx(
                'grid grid-cols-2 sm:grid-cols-3',
                'w-full gap-2 justify-center items-start'
              )}
            >
              <ThemeProvider theme={dateLabelBlack}>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="pt-br"
                >
                  <div className="col-span-2 sm:col-span-1">
                    <Datepicker
                      disabled={mode === ModeCalendar.view}
                      labelOut={false}
                      name="startDate"
                      label="Data início"
                      disablePast={user?.role !== UserRole.ADMIN}
                      rules={
                        user?.role === UserRole.ADMIN
                          ? eventRules.dateRequired
                          : eventRules.dateMinNowRequired
                      }
                    />
                  </div>

                  <Timepicker
                    labelOut={false}
                    disabled={mode === ModeCalendar.view}
                    name="startHour"
                    label="Horário início"
                    rules={eventRules.timeRequired}
                  />
                  <Timepicker
                    labelOut={false}
                    disabled={mode === ModeCalendar.view}
                    name="endHour"
                    label="Horário fim"
                    rules={eventRules.timeRequired}
                  />
                </LocalizationProvider>
              </ThemeProvider>
            </div>
            <ModalRecurrence
              mode={mode}
              disabled={mode === ModeCalendar.view || isEventBeforeNow}
            />
            <InputAutocomplete
              disabled={mode !== ModeCalendar.create}
              name="student"
              label="Nome do estudante:"
              CustomOnChange={(_, value) => {
                eventForm.setValue('student', value);
                eventForm.setValue(
                  'responsible',
                  handleResponsibleFullName(value?.id)
                );
              }}
              values={students}
              rules={eventRules.studentRequired}
            />
            <Input disabled label="Nome do Responsável:" name="responsible" />
            <InputAutocomplete
              disabled={mode !== ModeCalendar.create}
              name="teacher"
              label="Nome do professor:"
              CustomOnChange={(_, value) => {
                eventForm.resetField('subject');
                eventForm.setValue('teacher', value);
              }}
              values={teachers}
              rules={eventRules.teacherRequired}
            />
            <InputAutocomplete
              disabled={mode !== ModeCalendar.create}
              name="subject"
              key={teacher?.id}
              label="Matéria:"
              CustomOnChange={(_, value) => {
                eventForm.setValue('subject', value);
              }}
              values={selectSubjects}
              rules={eventRules.subjectRequired}
            />
            {mode !== ModeCalendar.view && (
              <Radio
                label="Tipo de Local:"
                name="placeType"
                options={[
                  { value: PlaceType.REMOTE, label: 'Remoto' },
                  {
                    value: PlaceType.PLUS_HEADQUARTER,
                    label: 'Plus aprendizagens',
                  },
                  { value: PlaceType.STUDENT_HOME, label: 'Casa do aluno' },
                ]}
                rules={eventRules.placeTypeRequired}
                direction="row"
              />
            )}
            <Input
              disabled={mode === ModeCalendar.view}
              label="Local:"
              name="place"
              rules={eventRules.placeRequired}
            />
            {showValueFromResponsible && (
              <Input
                label={'Valor da hora/aula a ser cobrado do responsável:'}
                name="valueFromResponsible"
                rules={
                  mode === ModeCalendar.create
                    ? eventRules.currencyValueGreaterThanZero
                    : eventRules.currencyValueRequired
                }
                onChange={(e) =>
                  handleChangeMoneyMask(e, 'valueFromResponsible')
                }
                disabled={mode === ModeCalendar.view}
                isLockable={mode !== ModeCalendar.view}
              />
            )}
            {showValueToTeacher && (
              <Input
                label={'Valor da hora/aula a ser pago ao professor:'}
                name="valueToTeacher"
                rules={
                  mode === ModeCalendar.create
                    ? eventRules.currencyValueGreaterThanZero
                    : eventRules.currencyValueRequired
                }
                onChange={(e) => handleChangeMoneyMask(e, 'valueToTeacher')}
                disabled={mode === ModeCalendar.view}
                isLockable={mode !== ModeCalendar.view}
              />
            )}
            {showValueToTrip && (
              <Input
                label={'Valor do deslocamento por aula:'}
                name="valueToTrip"
                rules={eventRules.currencyValueRequired}
                onChange={(e) => handleChangeMoneyMask(e, 'valueToTrip')}
                disabled={
                  mode === ModeCalendar.view || placeType === PlaceType.REMOTE
                }
                isLockable={
                  mode !== ModeCalendar.view && placeType !== PlaceType.REMOTE
                }
              />
            )}
            <TextArea
              disabled={mode === ModeCalendar.view}
              name={'description'}
              placeholder={'Descreva a aula e o conteúdo lecionado'}
              label={'Descrição e Conteúdo:'}
              onChange={(e) => {
                eventForm.setValue('description', e.currentTarget.value);
              }}
              rules={eventRules.descriptionRequired}
            />
            {(isAdminOrEmployee || isTeacher) && (
              <TextArea
                disabled={mode === ModeCalendar.view}
                label="Comentário para o professor:"
                name="teacherObservations"
                placeholder="Escreva algum comentário para o professor se necessário"
                onChange={(e) => {
                  eventForm.setValue(
                    'teacherObservations',
                    e.currentTarget.value
                  );
                }}
              />
            )}
            {mode !== ModeCalendar.view && (
              <Button
                isLoading={isLoading}
                type="submit"
                className="w-6/12 mt-6"
              >
                Salvar
              </Button>
            )}
          </form>
        </FormProvider>

        <UpdateClassDateModal
          mode={handleUpdateClass.mode}
          availableOptions={handleUpdateClass.availableDeleteTypeOptions}
          open={openUpdateClass && !openJustificationModal}
          onChoose={handleUpdateClass.func}
          onClose={() => setOpenUpdateClass(false)}
          isLoading={isLoading}
          lastJustification={changeValueJustification}
          needsJustification={needsValueJustification}
          paymentStatusesInRange={paymentStatusesInRange}
          needsClassTimeConflictConsent={hasConflict}
        />
      </DraggableModal>

      {reportInfoOpen && (
        <TeacherReport
          onChangeEvent={handleChangeEvent}
          reportInfo={reportInfo}
          onClose={() => {
            setReportInfo(undefined);
            setReportInfoOpen(false);
          }}
          title="Relatório"
          open={reportInfoOpen}
        />
      )}

      {/* Read-only modal for viewing justification */}
      <DraggableModal
        open={openJustificationModal}
        onClose={() => {
          setOpenJustificationModal(false);
        }}
      >
        <FormProvider {...eventForm}>
          <form>
            <div className="mt-2">
              <TextArea
                name="changeValueJustification"
                label="Justificativa para mudança nos valores:"
                disabled={true}
              />
            </div>
          </form>
        </FormProvider>
      </DraggableModal>

      {/* Used only for creating */}
      <ClassEventConfirmationModal
        mode="create"
        open={openCreateConflictBypassModal}
        onAccept={() => {
          const consentedConflictBypass =
            createClassConfirmationModalForm.getValues(
              'classTimeConflictConsent'
            );
          if (consentedConflictBypass) {
            eventForm.setValue('bypassConflict', true);
          }
          eventForm.handleSubmit(onSubmit)();
        }}
        onClose={() => {
          setOpenCreateConflictBypassModal(false);
        }}
        needsClassTimeConflictConsent={hasConflict}
        formContext={createClassConfirmationModalForm}
      />
    </>
  );
};
